/* Teacher start survey (Sandy 2026-09-30): a few questions BEFORE the
   teacher makes an account, then the signup itself (2026-10-26): the
   school, the account, the email code. The answers live in this browser
   (localStorage); the account fields never do. The school goes to
   claude-teacher-api setMySchool once the account exists.

   The curriculum question only SEARCHES the catalog we already mapped
   (ClaudeCurriculumCatalog, as the local copy the Curriculum Connect page
   uses). Nothing is uploaded or analyzed before an account exists (owner:
   AI mapping costs money and is not set up yet). A curriculum we do not
   have is kept as a request (publisher + course), so the team can label it
   and add the common ones to the list. */

// One source for the path: the nav's CTA reads it too.
export { TEACHER_SURVEY_PATH as SURVEY_PATH } from '../../../pages/marketingRoutes';
export const HOME_PATH = '/teachers';
// Where a teacher at a partner school signs up: with the school's teacher code.
export const SCHOOL_CODE_SIGNUP_PATH = '/signup';
export const DASHBOARD_PATH = '/teacher-dashboard';

const STORAGE_KEY = 'bb.teacherStart.answers';

export const SUBJECTS = [
  { id: 'math', label: 'Math', note: 'SAT Math' },
  { id: 'ela', label: 'English', note: 'SAT Reading & Writing' },
  { id: 'both', label: 'The whole SAT', note: 'Math and Reading & Writing, like an SAT prep class' },
];

export const SECTION_NAME = { math: 'SAT Math', ela: 'SAT Reading & Writing' };

// The landing's names for the SAT domains (F2 "Class results"), not the
// catalog's sentence-case ones, so the survey and the page say the same words.
export const DOMAIN_LABEL = {
  algebra: 'Algebra',
  'advanced-math': 'Advanced Math',
  'problem-solving-and-data-analysis': 'Problem Solving & Data Analysis',
  'geometry-and-trigonometry': 'Geometry & Trigonometry',
  'information-and-ideas': 'Information & Ideas',
  'craft-and-structure': 'Craft & Structure',
  'expression-of-ideas': 'Expression of Ideas',
  'standard-english-conventions': 'Standard English Conventions',
};

// Answers: { subject, pick } where pick is
//   { type: 'course', key } | { type: 'request', publisher, course } | { type: 'none' }
//   | { type: 'later' } (decides later, in Curriculum Connect; 2026-10-28)
export function loadAnswers() {
  try {
    const a = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    return a && typeof a === 'object' ? a : {};
  } catch {
    return {};
  }
}

export function saveAnswers(answers) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...answers, savedAt: new Date().toISOString() }));
  } catch { /* private mode: the answers last this visit */ }
}

// The catalog without unit detail, loaded on demand (its own chunk).
export async function loadCatalog() {
  const { listCurricula } = await import('../../../TeacherDashboardV3/curriculum/curriculumData');
  return listCurricula();
}

// The usual high-school order, so the short list starts with what most
// teachers teach; the program breaks ties.
const COURSE_ORDER = [
  'Algebra 1', 'Geometry', 'Algebra 2', 'Integrated Mathematics I', 'Integrated Mathematics II',
  'Integrated Mathematics III', 'AP Precalculus', 'AP Statistics', 'AP Calculus AB and BC',
  'Grade 9 English', 'Grade 10 English', 'AP English Language and Composition',
  'AP English Literature and Composition', 'AP Seminar',
];
const rank = (c) => {
  const i = COURSE_ORDER.indexOf(c.name);
  return i < 0 ? COURSE_ORDER.length : i;
};

export function coursesFor(catalog, subject) {
  if (!catalog) return [];
  return catalog.courses
    .filter((c) => subject === 'both' || c.subject === subject)
    .sort((a, b) => rank(a) - rank(b) || a.program.localeCompare(b.program));
}

// Every word of the query must appear in the course's name, other names,
// program or grades ("illustrative alg" finds Illustrative Mathematics Algebra 1).
export function matchesQuery(course, gradesText, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [course.name, ...(course.aliases || []), course.program, gradesText].join(' ').toLowerCase();
  return q.split(/\s+/).every((w) => hay.includes(w));
}

export const requestLabel = (pick) => [pick.publisher, pick.course].map((s) => (s || '').trim()).filter(Boolean).join(' ');

// The name the first class gets on the dashboard (the teacher can change it).
export function suggestedClassName(answers, catalog) {
  const pick = answers.pick || {};
  const course = pick.type === 'course' && catalog ? catalog.courses.find((c) => c.key === pick.key) : null;
  if (course) return course.name;
  if (pick.type === 'request' && (pick.course || '').trim()) return pick.course.trim();
  return { math: 'SAT Math', ela: 'SAT Reading & Writing', both: 'SAT Prep' }[answers.subject] || '';
}

/* ---------- the school (signup step 1, 2026-10-26) ----------
   Colorado high schools come from the CDE list the District Scores page
   uses (public/co-scores/index.json, 546 schools). Any other school,
   anywhere in the world, is added by hand. */

// Partner schools on that list (key = CDE district + school code) -> the
// name the school-code signup lists them under. Their teachers sign up with
// the school's teacher code. Mirrors PARTNER_CDE in claude-teacher-api
// schools.mjs, which refuses these schools on the server too.
export const PARTNER_CDE = {
  '00204108': 'Horizon', '00205816': 'Thornton', '01000118': 'Alamosa',
  '01808356': 'William Smith', '01809396': 'Aurora West Prep', '04702761': 'Erie',
  '05404216': 'Clear Creek', '09008847': 'ThunderRidge', '14200378': 'Arvada West',
  '14200664': 'Bear Creek', '14201522': 'Chatfield', '14201864': 'Columbine',
  '14202120': "D'Evelyn", '14204422': 'Jefferson', '14209510': 'Wheat Ridge',
};

export async function loadSchools() {
  const r = await fetch('/co-scores/index.json');
  if (!r.ok) throw new Error(`school list ${r.status}`);
  const d = await r.json();
  return d.districts.flatMap((dist) => (dist.schools || []).map((s) => ({
    key: `${dist.c}${s.c}`, district: dist.c, school: s.c, name: s.n, districtName: dist.n,
  })));
}

const fold = (s) => String(s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

// Every word of the query must start a word of the school or district name
// ("thunder" finds Thunderridge High School); name matches come first.
export function findSchools(list, query, max = 8) {
  const words = fold(query).split(' ').filter(Boolean);
  if (!list || !words.length) return [];
  const hits = [];
  for (const s of list) {
    const name = fold(s.name).split(' ');
    const all = name.concat(fold(s.districtName).split(' '));
    if (!words.every((w) => all.some((x) => x.startsWith(w)))) continue;
    hits.push({ s, rank: words.every((w) => name.some((x) => x.startsWith(w))) ? 0 : 1 });
  }
  return hits.sort((a, b) => a.rank - b.rank || a.s.name.localeCompare(b.s.name)).slice(0, max).map((h) => h.s);
}

// answers.school: { type: 'listed', key, name, districtName },
// { type: 'other', name, city, region, country }, or (2026-10-29) a
// colleague's invite link: { type: 'invite', code, name, place }.
export function schoolReady(school) {
  if (!school) return false;
  if (school.type === 'invite') return Boolean(school.code);
  if (school.type === 'listed') return Boolean(school.key) && !PARTNER_CDE[school.key];
  if (school.type === 'other') {
    return (school.name || '').trim().length >= 2 && Boolean((school.city || '').trim()) && Boolean(school.country)
      && (school.country !== US || Boolean(school.region));
  }
  return false;
}

// The body of claude-teacher-api setMySchool.
export function schoolPayload(school) {
  if (school.type === 'invite') return { invite: school.code };
  if (school.type === 'listed') return { cde: { district: school.key.slice(0, 4), school: school.key.slice(4) } };
  return { custom: { name: school.name.trim(), city: school.city.trim(), region: (school.region || '').trim(), country: school.country } };
}

// custom:SchoolName on the Cognito account ("<school> - <place>"), as the
// old Teacher Program signup wrote it; the sign-up notice reads it.
export function schoolLabel(school) {
  if (school.type === 'invite') return [school.name || 'Invited by a colleague', school.place].filter(Boolean).join(' - ').slice(0, 200);
  if (school.type === 'listed') return `${school.name} - CO`;
  const place = [school.city, school.region, school.country].map((s) => (s || '').trim()).filter(Boolean).join(', ');
  return `${school.name.trim()} - ${place}`.slice(0, 200);
}

export const US = 'United States';
export const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas',
  'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
  'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming',
];

// Every country by its English name (the browser's own list), United States
// first. A browser without Intl.DisplayNames gets a text field instead.
const REGION_CODES = ('AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BW BY BZ '
  + 'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR '
  + 'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE '
  + 'KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT '
  + 'MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU '
  + 'RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA '
  + 'UG US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW').split(' ');
export function countryNames() {
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    const names = REGION_CODES.map((c) => dn.of(c)).filter((n) => n && n !== US);
    return [US, ...[...new Set(names)].sort((a, b) => a.localeCompare(b))];
  } catch {
    return null;
  }
}

// Personal mail: allowed, with a nudge toward the school email (other
// teacher tools take any email; a school domain lets a school find its
// teachers later).
const PERSONAL_MAIL = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'icloud.com', 'me.com', 'mac.com', 'aol.com', 'proton.me', 'protonmail.com', 'gmx.com', 'gmx.net',
  'mail.com', 'yandex.com', 'zoho.com', 'qq.com', '163.com',
]);
export const isPersonalEmail = (email) => PERSONAL_MAIL.has(String(email || '').trim().toLowerCase().split('@')[1] || '');
export const looksLikeEmail = (email) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(email || '').trim());
